import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import CreateDriver from './CreateDriver';
import { formatPhone } from '../utils/formatters';

const PAGE_SIZE = 10;

export default function ManageDrivers({ user, operatorDetails, vendorOnly = false, editDriverId = null }) {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [showCreate, setShowCreate] = useState(false);
    const [editDriver, setEditDriver] = useState(null);
    const [toast, setToast] = useState(null);
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const [showImportScreen, setShowImportScreen] = useState(false);
    const [subscription, setSubscription] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const formatDateLocal = (d) => {
        if (!d) return null;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchDrivers = () => {
        setLoading(true);
        const url = user?.userType === 'operator'
            ? `/api/drivers/${user.operatorId}`
            : `/api/drivers/all`;

        fetch(url)
            .then(r => r.json())
            .then(data => {
                setDrivers(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        const handleHash = () => {
            const hashParts = window.location.hash.split('/');
            if (hashParts[0] === '#drivers' || hashParts[0] === '#vendor-drivers') {
                const id = hashParts[1];
                if (id && drivers.length > 0) {
                    const d = drivers.find(drv => drv._id === id);
                    if (d) {
                        setEditDriver(d);
                    }
                }
            }
        };

        handleHash();
        window.addEventListener('hashchange', handleHash);
        return () => window.removeEventListener('hashchange', handleHash);
    }, [drivers]);



    const downloadTemplate = () => {
        const templateData = [
            {
                'Name': 'John Doe',
                'Email': 'john@example.com',
                'Password': 'password123',
                'Phone Number': '501234567',
                'Country Code': '+971',
                'Alt Phone': '509876543',
                'Driver Code': 'D001',
                'Nationality': 'Indian',
                'Civil ID': '784-1234-1234567-1',
                'Civil ID Expiry': '2025-12-31',
                'Passport Number': 'L1234567',
                'Passport Expiry': '2028-10-10',
                'License Number': '123456',
                'License Expiry': '2026-05-20',
                'Onboard Date': '2023-01-15',
                'Status': 'Active',
                'Probation Period': '6 Months',
                'Accommodation Location': 'Dubai Silicon Oasis',
                'Base Salary': 3000,
                'Fixed Allowances': 500,
                'Overtime Rate': 15,
                'Uniform Cost': 100,
                'Accommodation Cost': 800,
                'Other Deduction': 50
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Drivers');
        XLSX.writeFile(wb, 'Drivers_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    name: row['Name'],
                    email: row['Email'],
                    password: row['Password'] || '123456',
                    phoneNumber: row['Phone Number'],
                    countryCode: row['Country Code'] || '+971',
                    altPhone: row['Alt Phone'],
                    driverCode: row['Driver Code'],
                    nationality: row['Nationality'],
                    civilId: row['Civil ID'],
                    civilIdExpiry: row['Civil ID Expiry'],
                    passportNumber: row['Passport Number'],
                    passportExpiry: row['Passport Expiry'],
                    licenseNumber: row['License Number'],
                    licenseExpiry: row['License Expiry'],
                    onboardDate: row['Onboard Date'],
                    status: row['Status'] || 'Active',
                    probationPeriod: row['Probation Period'],
                    accommodationLocation: row['Accommodation Location'],
                    baseSalary: row['Base Salary'] || 0,
                    fixedAllowances: row['Fixed Allowances'] || 0,
                    overtimeRate: row['Overtime Rate'] || 0,
                    uniformCost: row['Uniform Cost'] || 0,
                    accommodationCost: row['Accommodation Cost'] || 0,
                    otherDeduction: row['Other Deduction'] || 0
                }));

                const res = await fetch('/api/drivers/import', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operatorId: user?.operatorId, drivers: mapped })
                });
                const result = await res.json();
                if (res.ok) {
                    showToast(`${result.successCount} drivers imported!`);
                    fetchDrivers();
                } else {
                    showToast(result.message || 'Import failed', 'error');
                }
            } catch (err) {
                showToast('Format error', 'error');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    useEffect(() => {
        fetchDrivers();
        const opId = user?.operatorId;
        if (opId) {
            fetch(`/api/subscriptions/operator/${opId}`)
                .then(r => r.json())
                .then(data => {
                    if (Array.isArray(data)) {
                        const active = data.find(s => s.status === 'Active');
                        setSubscription(active || null);
                    }
                })
                .catch(() => {});
        }
    }, [user]);

    const filtered = useMemo(() => {
        if (!Array.isArray(drivers)) return [];
        const q = search.toLowerCase();
        let list = drivers;

        if (vendorOnly) {
            list = list.filter(d => d.vendorId);
        } else {
            // "Drivers" menu shows ONLY direct drivers (no vendor assigned)
            list = list.filter(d => !d.vendorId);
        }

        return list.filter(d =>
            (d.name || '').toLowerCase().includes(q) ||
            (d.email || '').toLowerCase().includes(q) ||
            (d.phoneNumber || '').includes(q) ||
            (d.idNumber || '').toLowerCase().includes(q) ||
            (d.driverCode || '').toLowerCase().includes(q)
        );
    }, [drivers, search, vendorOnly, user]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleDelete = async (driver) => {
        if (!window.confirm(`Delete driver "${driver.name}"?`)) return;
        try {
            const res = await fetch(`/api/drivers/${driver._id}`, { method: 'DELETE' });
            if (res.ok) {
                showToast('Driver deleted successfully');
                fetchDrivers();
            } else {
                showToast('Failed to delete driver', 'error');
            }
        } catch (err) {
            showToast('Server error', 'error');
        }
    };

    const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
        </svg>
    );

    const icons = {
        plus: 'M12 5v14 M5 12h14',
        search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
        edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
        trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
        file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6',
        user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
        check: 'M20 6L9 17l-5-5',
        clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        alert: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 17c-.77 1.333.192 3 1.732 3z',
        upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
        download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
        calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    };

    const stats = useMemo(() => {
        return {
            total: filtered.length,
            active: filtered.filter(d => d.status === 'Active').length,
            onLeave: filtered.filter(d => d.status === 'On Leave').length,
            inactive: filtered.filter(d => d.status === 'Inactive').length
        };
    }, [filtered]);

    if (showCreate || editDriver) {
        return (
            <div className="manage-drivers-page-form-active">
                <style>{`
                    .manage-drivers-page-form-active { background: #f8fafc; min-height: 100vh; }
                `}</style>
                <CreateDriver
                    user={user}
                    editDriver={editDriver}
                    vendorOnly={vendorOnly}
                    operatorDetails={operatorDetails}
                    onBack={() => { 
                        setShowCreate(false); 
                        setEditDriver(null); 
                        window.location.hash = vendorOnly ? '#vendor-drivers' : '#drivers';
                    }}
                    onSaved={() => { 
                        setShowCreate(false); 
                        setEditDriver(null); 
                        window.location.hash = vendorOnly ? '#vendor-drivers' : '#drivers';
                        fetchDrivers(); 
                    }}
                />
            </div>
        );
    }

    if (showImportScreen) {
        return (
            <ImportWizard
                operatorId={user?.operatorId}
                vendorOnly={vendorOnly}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchDrivers(); }}
            />
        );
    }

    return (
        <div className="manage-drivers-page">
            <style>{`
                .manage-drivers-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .theme-dark .manage-drivers-page { background: #0f172a; }

                .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                .title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .theme-dark .title { color: #f1f5f9; }

                .add-btn { 
                    background: #312e81; color: white; border: none; padding: 10px 20px; 
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s;
                }
                .add-btn:hover { background: #063A5C; transform: translateY(-1px); }

                /* Premium Buttons */
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-import {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                .btn-template {
                    background: linear-gradient(135deg, #00B2A9 0%, #16539C 100%);
                }
                .btn-primary {
                    background: #063A5C;
                    box-shadow: 0 4px 12px rgba(6, 58, 92, 0.25);
                }
                .premium-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 24px; }
                .stat-card {
                    background: white; border-radius: 16px; padding: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 16px; transition: all 0.25s;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03);
                }
                .theme-dark .stat-card { background: #1e293b; border-color: #334155; }
                .stat-card:hover { transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.08); }
                .stat-icon-wrap {
                    width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .stat-info { display: flex; flex-direction: column; }
                .stat-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px; }
                .stat-val { font-size: 24px; font-weight: 800; color: #1e293b; line-height: 1; }
                .theme-dark .stat-val { color: #f1f5f9; }

                .toolbar { 
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; 
                    background: white; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0;
                }
                .theme-dark .toolbar { background: #1e293b; border-color: #334155; }

                .search-box { position: relative; width: 300px; }
                .search-box input { 
                    width: 100%; padding: 10px 12px 10px 40px; border: 1px solid #e2e8f0; border-radius: 10px; 
                    font-size: 14px; outline: none; transition: all 0.2s;
                    background: #f8fafc;
                }
                .theme-dark .search-box input { background: #0f172a; border-color: #334155; color: white; }
                .search-box input:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
                .search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; }

                .table-container { 
                    background: white; border-radius: 16px; overflow: hidden; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                }
                .theme-dark .table-container { background: #1e293b; border-color: #334155; }

                .drivers-table { width: 100%; border-collapse: collapse; }
                .drivers-table th { 
                    background: #f8fafc; padding: 16px 20px; text-align: left; font-size: 12px; 
                    font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0;
                }
                .theme-dark .drivers-table th { background: #1a2233; color: #94a3b8; border-bottom-color: #334155; }

                .drivers-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #334155; }
                .theme-dark .drivers-table td { border-bottom-color: #334155; color: #cbd5e1; }
                .drivers-table tr:last-child td { border-bottom: none; }
                .drivers-table tr:hover { background: #fdfdff; }
                .theme-dark .drivers-table tr:hover { background: #242f45; }

                .driver-info { display: flex; align-items: center; gap: 12px; }
                .avatar { 
                    width: 36px; height: 36px; background: #e6f0fa; color: #11427D; border-radius: 50%;
                    display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px;
                }
                .driver-name { font-weight: 600; color: #1e293b; }
                .theme-dark .driver-name { color: #f1f5f9; }

                .status-badge {
                    padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase;
                }
                .status-active { background: #ecfdf5; color: #059669; }
                .status-under-review { background: #fef9c3; color: #92400e; }
                .status-inactive { background: #fef2f2; color: #dc2626; }
                .status-on-leave { background: #fffbeb; color: #d97706; }
                .status-resigned { background: #f3f4f6; color: #4b5563; }
                .status-terminated { background: #450a0a; color: #fecaca; }

                .actions { display: flex; gap: 10px; }
                .act-btn { 
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #64748b; transition: all 0.2s;
                }
                .act-btn:hover { background: #f1f5f9; color: #00B2A9; }
                .act-btn.delete:hover { background: #fee2e2; color: #ef4444; }
                .theme-dark .act-btn:hover { background: #334155; }

                .doc-link { color: #00B2A9; display: flex; align-items: center; gap: 4px; text-decoration: none; font-weight: 500; }
                .doc-link:hover { text-decoration: underline; }

                .pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 24px; }
                .page-info { font-size: 14px; color: #64748b; }
                .page-btns { display: flex; gap: 6px; }
                .p-btn { 
                    padding: 8px 14px; border: 1px solid #e2e8f0; background: white; border-radius: 8px;
                    font-size: 14px; cursor: pointer; transition: all 0.2s; color: #1e293b;
                }
                .p-btn:hover:not(:disabled) { border-color: #00B2A9; color: #00B2A9; }
                .p-btn.active { background: #312e81; color: white; border-color: #312e81; }
                .p-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                .theme-dark .p-btn { background: #1e293b; border-color: #334155; color: #94a3b8; }

                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 10px;
                    background: #1e293b; color: white; z-index: 1000; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    animation: slideIn 0.3s ease-out;
                }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
                @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header">
                <h1 className="title">{vendorOnly ? 'Outsourced Vendor Drivers' : 'Direct Fleet Drivers'}</h1>
                <div style={{ display: 'flex', gap: 12 }}>
                    <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleImport} />
                    {(() => {
                        const limit = subscription?.planId?.driversLimit !== undefined
                            ? subscription.planId.driversLimit
                            : subscription?.planId?.operatorsLimit;
                        const isUnlimited = !limit || limit === -1 || limit === '-1' || limit === 'Unlimited';
                        const currentCount = drivers.filter(d => (vendorOnly ? d.vendorId : !d.vendorId) && d.status === 'Active').length;
                        const atLimit = !isUnlimited && currentCount >= Number(limit);
                        return atLimit ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 12, background: '#fef2f2', border: '1.5px solid #fca5a5', color: '#dc2626', fontSize: 13, fontWeight: 700 }}>
                                ⚠️ Driver limit reached ({currentCount}/{limit}) — Please upgrade your plan
                            </div>
                        ) : (
                            <>
                                <button className="premium-btn btn-primary" onClick={() => setShowImportScreen(true)} disabled={importing}>
                                    <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                                    Import
                                </button>
                                {!vendorOnly && (
                                    <button className="premium-btn btn-primary" onClick={downloadTemplate}>
                                        <Icon d={icons.download} size={18} strokeWidth={2.5} />
                                        Template
                                    </button>
                                )}
                                <button className="premium-btn btn-primary" onClick={() => setShowCreate(true)} style={vendorOnly ? { background: '#10b981' } : { background: '#11427D' }}>
                                    <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                                    {vendorOnly ? 'Register Outsourced Driver' : 'Register Direct Driver'}
                                </button>
                            </>
                        );
                    })()}
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card" style={vendorOnly ? { borderLeft: '4px solid #10b981' } : {}}>
                    <div className="stat-icon-wrap" style={{ background: vendorOnly ? '#ecfdf5' : '#eef2ff' }}>
                        <Icon d={icons.user} color={vendorOnly ? '#059669' : '#11427D'} size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Total {vendorOnly ? 'Resources' : 'Drivers'}</span>
                        <span className="stat-val">{stats.total}</span>
                    </div>
                </div>
                <div className="stat-card" style={vendorOnly ? { borderLeft: '4px solid #10b981' } : {}}>
                    <div className="stat-icon-wrap" style={{ background: '#ecfdf5' }}>
                        <Icon d={icons.check} color="#059669" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Active</span>
                        <span className="stat-val">{stats.active}</span>
                    </div>
                </div>
                <div className="stat-card" style={vendorOnly ? { borderLeft: '4px solid #f59e0b' } : {}}>
                    <div className="stat-icon-wrap" style={{ background: '#fffbeb' }}>
                        <Icon d={icons.clock} color="#d97706" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">On Leave</span>
                        <span className="stat-val">{stats.onLeave}</span>
                    </div>
                </div>
                <div className="stat-card" style={vendorOnly ? { borderLeft: '4px solid #ef4444' } : {}}>
                    <div className="stat-icon-wrap" style={{ background: '#fef2f2' }}>
                        <Icon d={icons.alert} color="#dc2626" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Inactive</span>
                        <span className="stat-val">{stats.inactive}</span>
                    </div>
                </div>
            </div>

            <div className="toolbar">
                <div className="search-box">
                    <span className="search-icon"><Icon d={icons.search} size={18} /></span>
                    <input
                        type="text"
                        placeholder="Search drivers by name, email or ID..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                </div>
                <div className="filters">
                    {/* Add more filters if needed */}
                </div>
            </div>

            <div className="table-container">
                <table className="drivers-table">
                    <thead>
                        <tr>
                            <th>Driver</th>
                            {user?.userType === 'admin' && <th>Operator</th>}
                            {vendorOnly && <th>Vendor</th>}
                            <th>Driver ID</th>
                            <th>Contact Info</th>
                            <th>ID Number</th>
                            <th>Document</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={vendorOnly ? "9" : "8"} style={{ textAlign: 'center', padding: '60px' }}>Loading drivers...</td></tr>
                        ) : pageData.length === 0 ? (
                            <tr><td colSpan={vendorOnly ? "9" : "8"} style={{ textAlign: 'center', padding: '60px' }}>No drivers found.</td></tr>
                        ) : (
                            pageData.map(driver => {
                                const todayStr = formatDateLocal(new Date());
                                const activeLeaveToday = driver.leaves?.find(l => 
                                    l.status === 'Approved' && 
                                    l.startDate?.split('T')[0] <= todayStr && 
                                    l.endDate?.split('T')[0] >= todayStr
                                );
                                return (
                                <tr key={driver._id}>
                                    <td>
                                        <div className="driver-info">
                                            <div className="avatar">{driver.name?.charAt(0)}</div>
                                            <div className="driver-name" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <span>{driver.name}</span>
                                                {activeLeaveToday && (
                                                    <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '4px', alignSelf: 'flex-start', textTransform: 'uppercase' }}>
                                                        <Icon d={icons.edit} size={10} /> ON LEAVE TODAY
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    {user?.userType === 'admin' && (
                                        <td style={{ fontWeight: 600, color: '#16539C' }}>
                                            {driver.operatorId?.companyName || 'N/A'}
                                        </td>
                                    )}
                                    {vendorOnly && (
                                        <td style={{ fontWeight: 600, color: '#10b981' }}>
                                            {driver.vendorId?.vendorName || (typeof driver.vendorId === 'string' ? 'Loading...' : 'N/A')}
                                        </td>
                                    )}
                                    <td style={{ fontWeight: 700, color: '#16539C', background: '#eef2ff', borderRadius: '6px', textAlign: 'center' }}>
                                        {driver.driverCode || '-'}
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: 500, color: '#1e293b' }}>
                                            {formatPhone(driver.phoneNumber, operatorDetails?.country)}
                                        </div>
                                        <div style={{ color: '#64748b', fontSize: 13 }}>{driver.email}</div>
                                    </td>
                                    <td style={{ fontWeight: 500 }}>{driver.civilId || driver.idNumber || '-'}</td>
                                    <td>
                                        {driver.document ? (
                                            <a href={`/api/uploads/${driver.document}`} target="_blank" rel="noreferrer" className="doc-link">
                                                <Icon d={icons.file} size={14} />
                                                View Document
                                            </a>
                                        ) : (
                                            <span style={{ color: '#94a3b8' }}>No Document</span>
                                        )}
                                    </td>
                                    <td>
                                        {(() => {
                                            let displayStatus = driver.status;
                                            let badgeClass = `status-badge status-${driver.status?.toLowerCase().replace(' ', '-') || 'inactive'}`;
                                            
                                            if (activeLeaveToday) {
                                                displayStatus = activeLeaveToday.leaveType || 'On Leave';
                                                badgeClass = `status-badge status-on-leave`;
                                            }
                                            return (
                                                <span className={badgeClass}>
                                                    {displayStatus}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td>
                                        <div className="actions">
                                            <button className="act-btn" title="View Logins" onClick={() => {
                                                window.location.hash = `#driver-logins/${driver._id}`;
                                            }}>
                                                <Icon d={icons.calendar} size={16} />
                                            </button>
                                            <button className="act-btn" title="Edit" onClick={() => setEditDriver(driver)}>
                                                <Icon d={icons.edit} size={16} />
                                            </button>
                                            <button className="act-btn delete" title="Delete" onClick={() => handleDelete(driver)}>
                                                <Icon d={icons.trash} size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {!loading && filtered.length > PAGE_SIZE && (
                <div className="pagination">
                    <div className="page-info">
                        Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(filtered.length, page * PAGE_SIZE)} of {filtered.length} drivers
                    </div>
                    <div className="page-btns">
                        <button className="p-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                            <button
                                key={p}
                                className={`p-btn ${page === p ? 'active' : ''}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </button>
                        ))}
                        <button className="p-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ImportWizard({ operatorId, vendorOnly, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Name': 'John Doe',
                'Email': 'john@example.com',
                'Password': 'password123',
                'Phone Number': '501234567',
                'Country Code': '+971',
                'Alt Phone': '509876543',
                'Driver Code': 'D001',
                'Nationality': 'Indian',
                'Civil ID': '784-1234-1234567-1',
                'Civil ID Expiry': '2025-12-31',
                'Passport Number': 'L1234567',
                'Passport Expiry': '2028-10-10',
                'License Number': '123456',
                'License Expiry': '2026-05-20',
                'Onboard Date': '2023-01-15',
                'Status': 'Active',
                'Probation Period': '6 Months',
                'Accommodation Location': 'Dubai Silicon Oasis',
                'Base Salary': 3000,
                'Fixed Allowances': 500,
                'Overtime Rate': 15,
                'Uniform Cost': 100,
                'Accommodation Cost': 800,
                'Other Deduction': 50
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Drivers');
        XLSX.writeFile(wb, 'Drivers_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const mapped = parsedData.map(row => ({
                name: row['Name'],
                email: row['Email'],
                password: row['Password'] || '123456',
                phoneNumber: row['Phone Number'],
                countryCode: row['Country Code'] || '+971',
                altPhone: row['Alt Phone'],
                driverCode: row['Driver Code'],
                nationality: row['Nationality'],
                civilId: row['Civil ID'],
                civilIdExpiry: row['Civil ID Expiry'],
                passportNumber: row['Passport Number'],
                passportExpiry: row['Passport Expiry'],
                licenseNumber: row['License Number'],
                licenseExpiry: row['License Expiry'],
                onboardDate: row['Onboard Date'],
                status: row['Status'] || 'Active',
                probationPeriod: row['Probation Period'],
                accommodationLocation: row['Accommodation Location'],
                baseSalary: row['Base Salary'] || 0,
                fixedAllowances: row['Fixed Allowances'] || 0,
                overtimeRate: row['Overtime Rate'] || 0,
                uniformCost: row['Uniform Cost'] || 0,
                accommodationCost: row['Accommodation Cost'] || 0,
                otherDeduction: row['Other Deduction'] || 0
            }));

            const res = await fetch('/api/drivers/import', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operatorId, drivers: mapped })
            });
            const data = await res.json();
            if (res.ok) {
                setResult({ createdCount: data.successCount, updatedCount: 0, errors: data.errors || [] });
            } else {
                alert(data.message || 'Import failed');
            }
        } catch (err) {
            console.error(err);
            alert("Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    if (result) {
        return (
            <div className="manage-drivers-page" style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The driver data has been processed. New drivers were created.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', maxWidth: '700px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#065f46' }}>{result.updatedCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#047857', marginTop: '4px' }}>Drivers Updated</div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>New Drivers Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>
                    {result.errors && result.errors.length > 0 && (
                        <div style={{ maxWidth: '750px', margin: '0 auto 30px', textAlign: 'left' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>Details / Warnings:</h4>
                            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
                                {result.errors.map((err, idx) => (
                                    <div key={idx} style={{ fontSize: '13px', color: '#64748b', padding: '6px 0', borderBottom: idx < result.errors.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                        ⚠️ Row {err.row}: {err.message}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <button onClick={onDone} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}>
                        Return to Drivers
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-drivers-page" style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import {vendorOnly ? 'Outsourced Vendor Drivers' : 'Direct Fleet Drivers'}</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new drivers via Excel upload.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>ℹ️ How it works</h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li>All valid rows will automatically be created as brand new drivers!</li>
                            </ul>
                        </div>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {['Name', 'Email', 'Password', 'Phone Number', 'Country Code', 'Alt Phone', 'Driver Code', 'Nationality', 'Civil ID', 'Civil ID Expiry', 'Passport Number', 'Passport Expiry', 'License Number', 'License Expiry', 'Onboard Date', 'Status', 'Probation Period', 'Accommodation Location', 'Base Salary', 'Fixed Allowances', 'Overtime Rate', 'Uniform Cost', 'Accommodation Cost', 'Other Deduction'].map((f, i) => (
                                    <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: '6px' }}>{f}</span>
                                ))}
                            </div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button onClick={downloadTemplate} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>
                    <div
                        onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                        style={{
                            background: dragActive ? '#f0f7ff' : 'white', borderRadius: '16px', border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', transition: 'all 0.15s', cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('file-upload-input').click()}
                    >
                        <input id="file-upload-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>Supports .xlsx, .xls formats</span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{file?.name}</span>
                                <span style={{ fontSize: '12px', color: '#64748b', background: '#cbd5e1', padding: '2px 8px', borderRadius: '999px', fontWeight: '600' }}>{parsedData.length} Rows Found</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => { setFile(null); setParsedData([]); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>Change File</button>
                            <button onClick={handleImport} disabled={importing} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 22px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}>
                                {importing ? 'Processing...' : `Proceed Import (${parsedData.length})`}
                            </button>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Name</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Email</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Phone Number</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((d, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                                        <td style={{ padding: '14px 20px', fontWeight: '700', color: '#1e293b' }}>{d['Name'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d['Email'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d['Country Code'] || ''} {d['Phone Number'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d['Status'] || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}



